import { apiFetch, apiFetchJson } from "@rook/shared/lib/apiFetch";

import type {
  ForgotPasswordInput,
  LoginInput,
  ResetPasswordInput,
  SignupAttributesInput,
  SignupCredentialsInput,
} from "./schemas";
import { SIGNUP_USER_ACCEPTANCE_DOCS } from "../legal/legalDocuments";


/**
 * Auth BFF API wrappers. Each function maps 1-to-1 to a Lambda in
 * `amplify/functions/auth-*`. They return the Lambda's success
 * payload directly and let `apiFetch` throw `ApiError` for the
 * failure paths — TanStack Query mutations catch + surface the
 * error.message in the form-level Notification.
 */

type LoginResponse = {
  ok: true;
  email: string;
  expiresAt?: string;
};

export const login = ({ email, password }: LoginInput) =>
  apiFetchJson<LoginResponse>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });

export const logout = () =>
  apiFetch("/api/auth/logout", { method: "POST" });

type ForgotPasswordResponse = {
  ok: true;
};

export const forgotPassword = ({ email }: ForgotPasswordInput) =>
  apiFetchJson<ForgotPasswordResponse>("/api/auth/forgot", {
    method: "POST",
    body: JSON.stringify({ username: email }),
  });

type ResetPasswordResponse = {
  ok: true;
};

export const resetPassword = ({ email, code, password }: ResetPasswordInput) =>
  apiFetchJson<ResetPasswordResponse>("/api/auth/reset", {
    method: "POST",
    body: JSON.stringify({ username: email, code, password }),
  });

type RegisterResponse = {
  User: { Username: string; UserStatus: string; Attributes?: unknown };
};

export const register = ({ email, password }: SignupCredentialsInput) =>
  apiFetchJson<RegisterResponse>("/api/auth/register", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });

/**
 * Update Cognito custom + standard attributes for a freshly
 * registered user. The BFF re-authenticates with the email+password
 * (the user isn't logged in yet) before issuing the update — same
 * flow as the legacy.
 */
export const updateAttributes = (
  attributes: Omit<SignupAttributesInput, "acceptedLegal">,
  credentials: { email: string; password: string },
) =>
  apiFetch("/api/auth/update-attributes", {
    method: "POST",
    body: JSON.stringify({ data: attributes, account: credentials }),
  });

type AcceptContractResponse = {
  data: { pk: string; sk: string; docSha256: string; acceptedAt: string };
};

/**
 * Record ONE clickwrap acceptance in the audit trail
 * (cpf-contract-acceptances → DynamoDB). Requires a live session — the
 * BFF reads `sub`/`email` from the cookie, never the body.
 */
export const acceptContract = (input: {
  slug: string;
  version: string;
  acceptanceMoment: "signup" | "paid-activation";
}) =>
  apiFetchJson<AcceptContractResponse>("/api/cpf/contracts/accept", {
    method: "POST",
    body: JSON.stringify(input),
  });

/**
 * Record the user-scope signup acceptances (Privacy, ToS, Master
 * Agreement per SIGNUP_USER_ACCEPTANCE_DOCS) once the session exists.
 *
 * Best-effort by design: the enforced signup checkbox IS the legal act;
 * this writes the audit row. A transient DynamoDB/API failure is logged
 * but never thrown, so it can't trap a customer out of their freshly
 * created account. Rare misses are recoverable (next-login re-record /
 * reconciliation — see foundations/PENDING-WORK.md).
 */
export const recordSignupAcceptances = async (): Promise<void> => {
  await Promise.all(
    SIGNUP_USER_ACCEPTANCE_DOCS.map(async (doc) => {
      try {
        await acceptContract({
          slug: doc.slug,
          version: doc.version,
          acceptanceMoment: "signup",
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(
          `[signup] failed to record acceptance for ${doc.slug}@${doc.version}`,
          err,
        );
      }
    }),
  );
};
